import React from 'react';
import { DateRangePicker, DateRangePickerProps } from '@mui/x-date-pickers-pro/DateRangePicker';
import { SingleInputDateRangeField } from '@mui/x-date-pickers-pro/SingleInputDateRangeField';
import { PickersCalendarHeaderProps } from '@mui/x-date-pickers/PickersCalendarHeader';
import { PickersActionBarProps } from '@mui/x-date-pickers/PickersActionBar';
import { PickersLayout, PickersLayoutProps } from '@mui/x-date-pickers/PickersLayout';
import { useTheme } from '@mui/material';
import dayjs, { Dayjs } from 'dayjs';
import { IconButton } from '../IconButton';
import { Box } from '../Box';
import { Flex } from '../Flex';
import { Button } from '../Button';
import { Icon } from '../Icon';
import { Text } from '../Text';
import { Tooltip } from '../Tooltip';
import type { TextInputProps } from '../TextInput';
import {
  PounceStyleProps,
  extractPounceProps,
  mapPouncePropsToSx,
  getEffectiveDateFormat,
} from '../../utils';
import { TimePicker } from '../DateInput/TimePicker';
import { Presets, Preset, defaultPresets, PresetPrecision } from './Presets';

// Custom calendar header showing two months side by side with navigation
const CustomCalendarHeader = (
  props: PickersCalendarHeaderProps & { position?: 'start' | 'end' },
) => {
  const { onMonthChange, currentMonth } = props;
  // Access internal MUI props to determine calendar position
  const internalProps = props as any;
  const isStartCalendar = internalProps.calendars === 2 && internalProps.monthIndex === 0;
  const isEndCalendar = internalProps.calendars === 2 && internalProps.monthIndex === 1;

  return (
    <Box
      sx={{
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        paddingX: '4px',
        paddingY: '12px',
      }}
    >
      {/* Only show left arrow on the first (left) calendar */}
      {!isEndCalendar && (
        <Button
          variant="ghost"
          size="medium"
          aria-label="Go to previous month"
          onClick={() => onMonthChange(currentMonth.subtract(1, 'month'))}
        >
          <Icon type="chevron-left" />
        </Button>
      )}
      {isEndCalendar && <Box sx={{ width: '40px' }} />}

      {!isEndCalendar && (
        <Text
          variant="label/lg"
          fontWeight="semibold"
          sx={{
            flex: 1,
            textAlign: 'center',
          }}
        >
          {currentMonth.format('MMMM YYYY')}
        </Text>
      )}

      {isEndCalendar && (
        <Text
          variant="label/lg"
          fontWeight="semibold"
          sx={{
            width: '50%',
            textAlign: 'center',
          }}
        >
          {currentMonth.add(1, 'month').format('MMMM YYYY')}
        </Text>
      )}

      {/* Only show right arrow on the second (right) calendar */}
      {isStartCalendar && <Box sx={{ width: '40px' }} />}
      {!isStartCalendar && (
        <Button
          variant="ghost"
          size="medium"
          aria-label="Go to next month"
          onClick={() => onMonthChange(currentMonth.add(1, 'month'))}
        >
          <Icon type="chevron-right" />
        </Button>
      )}
    </Box>
  );
};

// Custom layout component that includes presets
const CustomLayout = (
  allProps: PickersLayoutProps<any> & {
    withPresets?: boolean;
    presets?: Preset[];
    timezone?: 'local' | 'utc';
    onPresetSelect?: (dates: [Dayjs, Dayjs], preset: Preset) => void;
    precision?: PresetPrecision;
    selectedPresetId?: string | null;
  },
) => {
  const {
    withPresets,
    presets,
    timezone,
    onPresetSelect,
    precision,
    selectedPresetId,
    ...pickerLayoutProps
  } = allProps;

  if (!withPresets) {
    return <PickersLayout {...pickerLayoutProps} />;
  }

  return (
    <Box sx={{ display: 'flex', flexDirection: 'row', alignItems: 'stretch' }}>
      <Presets
        timezone={timezone || 'local'}
        presets={presets || defaultPresets}
        onSelect={onPresetSelect || (() => {})}
        precision={precision}
        selectedPresetId={selectedPresetId}
      />
      <PickersLayout {...pickerLayoutProps} />
    </Box>
  );
};

// Custom action bar that includes TimePicker when withTime is enabled
interface CustomActionBarProps extends PickersActionBarProps {
  withTime?: boolean;
  mode?: '12h' | '24h';
  disableSelection?: (date: Dayjs) => boolean;
  timezone?: 'local' | 'utc';
  // Time update handlers passed from parent to update draft state
  onStartTimeUpdate?: (updatedDate: Dayjs | null) => void;
  onEndTimeUpdate?: (updatedDate: Dayjs | null) => void;
  // Draft values passed from parent
  startValue?: Dayjs | null;
  endValue?: Dayjs | null;
  // Allow applying when values are unchanged
  allowApplyWhenUnchanged?: boolean;
  // Validation state for draft dates
  areDraftDatesValid?: boolean;
  // Whether draft values differ from committed values
  hasChanges?: boolean;
  // Custom handlers
  onApplyClick?: () => void;
  onCancelClick?: () => void;
  onClearClick?: () => void;
}

const CustomActionBar = (props: CustomActionBarProps) => {
  const {
    withTime,
    mode,
    disableSelection,
    onStartTimeUpdate,
    onEndTimeUpdate,
    startValue,
    endValue,
    allowApplyWhenUnchanged,
    areDraftDatesValid,
    hasChanges,
    onApplyClick,
    onCancelClick,
    onClearClick,
    ...actionBarProps
  } = props;
  const theme = useTheme();
  const componentTokens = theme.palette?.componentTokens;

  // Extract actions to determine if clear button should be shown
  const actions = (actionBarProps as any).actions || ['cancel', 'accept', 'clear'];
  const hasClearButton = actions.includes('clear');

  // Disable Apply button if:
  // 1. Dates are invalid (use passed validation state)
  // 2. Values are unchanged (unless allowApplyWhenUnchanged is true)
  const isApplyDisabled = !areDraftDatesValid || (!allowApplyWhenUnchanged && !hasChanges);

  return (
    <>
      {withTime && disableSelection && onStartTimeUpdate && onEndTimeUpdate && (
        <Flex
          align="center"
          spacing={3}
          sx={{
            p: '16px',
            border: '1px solid',
            borderBottom: 'none',
            borderColor: componentTokens?.calendar.colors.container.stroke.default,
            backgroundColor: componentTokens?.calendar.colors.container.background.default,
            justifyContent: 'space-between',
          }}
        >
          <TimePicker
            date={startValue}
            mode={mode}
            onTimeUpdate={onStartTimeUpdate}
            disableSelection={disableSelection}
            label="From Time"
          />
          <TimePicker
            date={endValue}
            mode={mode}
            onTimeUpdate={onEndTimeUpdate}
            disableSelection={disableSelection}
            label="To Time"
          />
        </Flex>
      )}
      <Box
        sx={{
          display: 'flex',
          flexGrow: 1,
          alignItems: 'center',
          justifyContent: hasClearButton ? 'space-between' : 'end',
          backgroundColor: componentTokens?.calendar.colors.container.background.default,
          border: '1px solid',
          borderColor: componentTokens?.calendar.colors.container.stroke.default,
          borderBottomRightRadius: '6px',
          padding: '16px',
        }}
      >
        <Flex flexGrow={1} justify="space-between">
          <Box>
            {hasClearButton && (
              <Button
                size="medium"
                variant="link-secondary"
                onClick={() => {
                  if (onClearClick) {
                    onClearClick();
                  }
                }}
                sx={{ p: 0 }}
              >
                {withTime ? 'Clear Dates & Time' : 'Clear Dates'}
              </Button>
            )}
          </Box>
          <Flex direction="row-reverse" gap={2}>
            <Button
              size="medium"
              variant="secondary"
              disabled={isApplyDisabled}
              onClick={() => {
                if (onApplyClick) {
                  onApplyClick();
                }
              }}
            >
              Apply
            </Button>
            <Button
              size="medium"
              variant="ghost"
              onClick={() => {
                if (onCancelClick) {
                  onCancelClick();
                }
              }}
            >
              Cancel
            </Button>
          </Flex>
        </Flex>
      </Box>
    </>
  );
};

export interface DateRangeInputProps
  extends
    Omit<
      DateRangePickerProps,
      'value' | 'onChange' | 'format' | 'minDate' | 'maxDate' | keyof PounceStyleProps
    >,
    Pick<TextInputProps, 'invalid'>,
    PounceStyleProps {
  /**
   * The position to expand the picker
   */
  alignment?: 'left' | 'right' | 'match-width';

  /**
   * The format displayed in the input elements
   */
  format?: string;

  /**
   * The format displayed in the start input element. If not provided, the format prop will be used.
   */
  fromFormat?: string;

  /**
   * A flag that allows clearing the values
   */
  disableReset?: boolean;

  /**
   * This variable is used to render additional content at the end of the input (useful for icons)
   */
  inputEndContent?: React.ReactNode;

  /**
   * The minimum date that a user is allowed to select
   */
  minDate?: Date;

  /**
   * The maximum date that a user is allowed to select
   */
  maxDate?: Date;

  /**
   * Date range input format for time picker
   */
  mode?: '12h' | '24h';

  /**
   * A list of preset entries that will appear in the DateRangePicker.
   */
  presets?: Preset[];

  /**
   * Whether the component should show presets
   */
  withPresets?: boolean;

  /**
   * A flag that dictates if the component should allow manipulating time
   */
  withTime?: boolean;

  /**
   * A flag that dictates if the component should show the icon toggle next to the input
   */
  withIcon?: boolean;

  /**
   * The label for the range start input
   */
  labelStart: string;

  /**
   * The label for the range end input
   */
  labelEnd: string;

  /**
   * The placeholder for the range start input
   */
  placeholderStart?: string;

  /**
   * The placeholder for the range end input
   */
  placeholderEnd?: string;

  /**
   * A date range that works as a value
   */
  value?: [Date?, Date?];

  /**
   * Specifies the timezone that will be used when selecting dates
   */
  timezone?: 'local' | 'utc';

  /**
   * A callback for whenever the value of the chosen date range changes.
   */
  onChange: (date: [Date?, Date?]) => void;

  /**
   * A render prop that allows you to customize the input element
   * defaults to a SingleInputDateRangeField
   */
  renderInput?: (props: RenderInputProps) => React.ReactNode;

  /**
   * A callback that is triggered when a preset is selected
   */
  onPresetSelect?: (preset: Preset) => void;

  /**
   * A ref for the inner DateWrapper component
   */
  dateWrapperRef?: React.RefObject<HTMLDivElement>;

  /**
   * Whether the component should be open by default
   */
  initialIsOpen?: boolean;

  /**
   * A callback that is triggered when the apply button is clicked
   */
  onApply?: (dates: [Date?, Date?]) => void;

  /**
   * A callback that is triggered when the cancel button is clicked
   */
  onCancel?: () => void;

  /**
   * A flag that dictates if the component should allow applying when the dates are unchanged
   */
  allowApplyWhenUnchanged?: boolean;

  /**
   * Whether the input is disabled
   */
  disabled?: boolean;

  /**
   * Whether the input has a validation error
   */
  invalid?: boolean;

  /**
   * Additional styles to apply (combined with pounce props)
   */
  sx?: DateRangePickerProps['sx'];
}

type RenderInputProps = Pick<
  DateRangeInputProps,
  'placeholderStart' | 'placeholderEnd' | 'labelStart' | 'labelEnd' | 'disabled'
> & {
  openDatePicker: () => void;
  fromFormattedValue?: string;
  toFormattedValue?: string;
};

/**
 * Convert Date to Dayjs with timezone support
 */
const dateToDayjs = (date: Date | undefined, timezone: 'local' | 'utc' = 'local'): Dayjs | null => {
  if (!date) {
    return null;
  }
  if (timezone === 'utc') {
    // For UTC mode, create a dayjs object with UTC components as local time
    // This displays the UTC date/time values in the picker without timezone conversion
    return dayjs()
      .year(date.getUTCFullYear())
      .month(date.getUTCMonth())
      .date(date.getUTCDate())
      .hour(date.getUTCHours())
      .minute(date.getUTCMinutes())
      .second(date.getUTCSeconds())
      .millisecond(date.getUTCMilliseconds());
  }
  return dayjs(date);
};

/**
 * A component to help selecting date ranges in forms.
 * Built on top of MUI-x DateRangePicker with the same API as the original pounce DateRangeInput.
 */
export const DateRangeInput = React.forwardRef<HTMLDivElement, DateRangeInputProps>(
  function DateRangeInput(
    {
      value = [],
      format = 'MM/DD/YYYY',
      fromFormat = format,
      mode = '24h',
      withTime,
      disableReset = false,
      minDate,
      maxDate,
      presets = defaultPresets,
      withPresets,
      onChange,
      labelStart,
      alignment = 'left',
      labelEnd,
      inputEndContent,
      placeholderStart,
      placeholderEnd,
      timezone = 'local',
      withIcon = true,
      renderInput,
      onPresetSelect,
      dateWrapperRef,
      initialIsOpen = false,
      onApply,
      onCancel,
      allowApplyWhenUnchanged = false,
      disabled = false,
      invalid,
      sx,
      ...rest
    },
    ref,
  ) {
    const theme = useTheme();
    const { pounceProps, otherProps } = extractPounceProps(rest);
    const pounceStyles = mapPouncePropsToSx(pounceProps);

    // State to control picker open/close ONLY when using renderInput
    const [pickerOpen, setPickerOpen] = React.useState(initialIsOpen);

    // Sync pickerOpen with initialIsOpen prop changes (for controlled usage like TimeframeSelectionV3)
    React.useEffect(() => {
      setPickerOpen(initialIsOpen);
    }, [initialIsOpen]);

    // State to track selected preset
    const [selectedPresetId, setSelectedPresetId] = React.useState<string | null>(null);

    // Ref to track if change is from preset selection
    const isPresetChangeRef = React.useRef(false);

    // Ref to suppress the MUI ClickAwayListener close that fires when the user
    // clicks on the text input field while the picker is already open.
    // The text field's onMouseDown fires before the document-level ClickAwayListener,
    // so we set this flag there and clear it inside onClose.
    const ignoreNextCloseRef = React.useRef(false);

    // Draft state - holds the temporary selection until user clicks Apply
    const [draftValue, setDraftValue] = React.useState<[Dayjs | null, Dayjs | null]>([null, null]);

    // Compute effective format with time tokens when withTime is enabled
    const effectiveFormat = React.useMemo(
      () => getEffectiveDateFormat(format, withTime, mode),
      [format, withTime, mode],
    );

    const effectiveFromFormat = React.useMemo(
      () => getEffectiveDateFormat(fromFormat, withTime, mode),
      [fromFormat, withTime, mode],
    );

    // Convert Date values to Dayjs (the committed/external value)
    const dayjsValue = React.useMemo(
      (): [Dayjs | null, Dayjs | null] => [
        dateToDayjs(value[0], timezone),
        dateToDayjs(value[1], timezone),
      ],
      [value, timezone],
    );

    // Sync draft value when external value changes
    React.useEffect(() => {
      setDraftValue(dayjsValue);
    }, [dayjsValue]);

    // Memoized validation check for draft dates
    const areDraftDatesValid = React.useMemo(
      () =>
        draftValue[0] &&
        draftValue[1] &&
        draftValue[0].isValid() &&
        draftValue[1].isValid() &&
        !draftValue[0].isAfter(draftValue[1]),
      [draftValue],
    );

    // Memoized comparison to check if draft differs from committed values
    const hasChanges = React.useMemo(() => {
      const precision = withTime ? 'minute' : 'day';
      const areBothDraftUndefined = !draftValue[0] && !draftValue[1];
      const areBothCommittedUndefined = !dayjsValue[0] && !dayjsValue[1];

      // If both are undefined, no changes
      if (areBothDraftUndefined && areBothCommittedUndefined) {
        return false;
      }
      // If only one pair is undefined, there are changes
      if (areBothDraftUndefined !== areBothCommittedUndefined) {
        return true;
      }
      // Compare start values
      const startSame =
        (draftValue[0] && dayjsValue[0] && draftValue[0].isSame(dayjsValue[0], precision)) ||
        (!draftValue[0] && !dayjsValue[0]);
      // Compare end values
      const endSame =
        (draftValue[1] && dayjsValue[1] && draftValue[1].isSame(dayjsValue[1], precision)) ||
        (!draftValue[1] && !dayjsValue[1]);
      // Return true if there are changes (not same)
      return !(startSame && endSame);
    }, [draftValue, dayjsValue, withTime]);

    // Track previous pickerOpen state to detect transitions
    const prevPickerOpenRef = React.useRef(pickerOpen);
    React.useEffect(() => {
      // Handle side effects when picker closes
      if (!pickerOpen && prevPickerOpenRef.current) {
        // Don't reset draft value - keep pending changes visible
        setSelectedPresetId(null);
        if (onCancel) {
          onCancel();
        }
      }
      prevPickerOpenRef.current = pickerOpen;
    }, [pickerOpen, onCancel]);
    const dayjsMinDate = React.useMemo(() => dateToDayjs(minDate, timezone), [minDate, timezone]);
    const dayjsMaxDate = React.useMemo(() => dateToDayjs(maxDate, timezone), [maxDate, timezone]);

    // Format the current values for display (using committed value, not draft)
    const fromFormattedValue = React.useMemo(() => {
      if (!dayjsValue[0] || !dayjsValue[0].isValid()) {
        return '';
      }
      try {
        return dayjsValue[0].format(effectiveFromFormat);
      } catch (error) {
        return '';
      }
    }, [dayjsValue, effectiveFromFormat]);

    const toFormattedValue = React.useMemo(() => {
      if (!dayjsValue[1] || !dayjsValue[1].isValid()) {
        return '';
      }
      try {
        return dayjsValue[1].format(effectiveFormat);
      } catch (error) {
        return '';
      }
    }, [dayjsValue, effectiveFormat]);

    // Helper to convert Dayjs to Date with proper timezone handling
    const convertDayjsToDate = React.useCallback(
      (date: Dayjs | null, isEndDate: boolean): Date | undefined => {
        if (date === null) {
          return undefined;
        }
        const precision = withTime ? 'minute' : 'day';
        const rounded = isEndDate ? date.endOf(precision) : date.startOf(precision);
        if (timezone === 'utc') {
          return new Date(
            Date.UTC(
              rounded.year(),
              rounded.month(),
              rounded.date(),
              rounded.hour(),
              rounded.minute(),
              rounded.second(),
              rounded.millisecond(),
            ),
          );
        }
        return rounded.toDate();
      },
      [withTime, timezone],
    );

    // Handle change event from date range picker - only updates draft state
    const handleChange = React.useCallback((newValue: [Dayjs | null, Dayjs | null]) => {
      // Clear preset selection if this is a manual date change
      if (!isPresetChangeRef.current) {
        setSelectedPresetId(null);
      }
      isPresetChangeRef.current = false;

      // Only update the draft value, don't call onChange yet
      setDraftValue(newValue);
    }, []);

    // Handle accept event - called when user clicks Apply
    const handleAccept = React.useCallback(
      (acceptedValue: [Dayjs | null, Dayjs | null]) => {
        const [start, end] = acceptedValue;
        const startDate = convertDayjsToDate(start, false);
        const endDate = convertDayjsToDate(end, true);
        const dates: [Date?, Date?] = [startDate, endDate];

        if (onApply) {
          onApply(dates);
        }
        onChange(dates);
      },
      [convertDayjsToDate, onApply, onChange],
    );

    // Check if date selection is disabled
    const shouldDenySelectedDate = React.useCallback(
      (selectedDate: Dayjs) => {
        // Return true (deny) if date is invalid
        if (!selectedDate || !selectedDate.isValid()) {
          return true;
        }
        return (
          (minDate && selectedDate?.isBefore(minDate)) ||
          (maxDate && selectedDate?.isAfter(maxDate))
        );
      },
      [minDate, maxDate],
    );

    // Handle preset selection - updates draft state
    const handlePresetSelect = React.useCallback(
      (dates: [Dayjs, Dayjs], preset: Preset) => {
        setSelectedPresetId(preset.id);
        isPresetChangeRef.current = true;
        // Update draft value with preset selection
        setDraftValue(dates);
        if (onPresetSelect) {
          onPresetSelect(preset);
        }
      },
      [onPresetSelect],
    );

    // Handle start time update - updates draft state only
    const handleStartTimeUpdate = React.useCallback((updatedDate: Dayjs | null) => {
      if (updatedDate && !updatedDate.isValid()) {
        return;
      }
      setDraftValue(prev => [updatedDate, prev[1]]);
    }, []);

    // Handle end time update - updates draft state only
    const handleEndTimeUpdate = React.useCallback((updatedDate: Dayjs | null) => {
      if (updatedDate && !updatedDate.isValid()) {
        return;
      }
      setDraftValue(prev => [prev[0], updatedDate]);
    }, []);

    // Compute valid start/end values for TimePicker display
    const now = React.useMemo(() => (timezone === 'utc' ? dayjs.utc() : dayjs()), [timezone]);
    const startValueForTimePicker = React.useMemo(() => {
      if (draftValue[0] && draftValue[0].isValid()) {
        return draftValue[0];
      }
      return withTime ? now : null;
    }, [draftValue, withTime, now]);
    const endValueForTimePicker = React.useMemo(() => {
      if (draftValue[1] && draftValue[1].isValid()) {
        return draftValue[1];
      }
      return withTime ? now : null;
    }, [draftValue, withTime, now]);

    // Combine pounce styles with user sx
    const combinedSx = {
      ...pounceStyles,
      ...(sx || {}),
    };

    // Calculate popper placement based on alignment
    const popperConfig = React.useMemo(() => {
      switch (alignment) {
        case 'right':
          return {
            placement: 'bottom-end' as const,
            matchWidth: false,
          };
        case 'match-width':
          return {
            placement: 'bottom-start' as const,
            matchWidth: true,
          };
        case 'left':
        default:
          return {
            placement: 'bottom-start' as const,
            matchWidth: false,
          };
      }
    }, [alignment]);

    const commonProps = {
      ref,
      value: draftValue,
      onChange: handleChange,
      format: effectiveFormat,
      minDate: dayjsMinDate,
      maxDate: dayjsMaxDate,
      disabled,
      closeOnSelect: false,
      calendars: 2,
      // Don't use MUI's timezone prop to avoid dependency on UTC plugin
      desktopModeMediaQuery: '@media (pointer: fine), (pointer: coarse)',
      // Force desktop mode which uses Popper (shows 2 calendars) instead of Dialog (shows 1 calendar)
      displayStaticWrapperAs: 'desktop' as const,
      sx: combinedSx,
      dayOfWeekFormatter: (day: Dayjs) => day.format('dd'),
      open: pickerOpen,
      onOpen: () => setPickerOpen(true),
      onClose: () => {
        // Ignore the close triggered by MUI's ClickAwayListener when the user clicks the
        // text input field while the picker is open. The text field onMouseDown sets this
        // flag before the document-level ClickAwayListener fires.
        if (ignoreNextCloseRef.current) {
          ignoreNextCloseRef.current = false;
          return;
        }
        setPickerOpen(false);
      },
      localeText: {
        okButtonLabel: 'Apply',
        cancelButtonLabel: 'Cancel',
        clearButtonLabel: withTime ? 'Clear Dates & Time' : 'Clear Dates',
      },
      slots: {
        field: SingleInputDateRangeField,
        openPickerButton:
          withIcon || inputEndContent
            ? (props: any) => {
                return (
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: '4px' }}>
                    {inputEndContent}

                    {hasChanges && (
                      <Tooltip
                        content={!areDraftDatesValid ? 'Invalid date range' : 'Apply changes'}
                        placement="top"
                      >
                        <Box component="span">
                          <IconButton
                            icon="check"
                            size="small"
                            variant="ghost"
                            aria-label="Apply changes"
                            disabled={!areDraftDatesValid}
                            onClick={e => {
                              e.stopPropagation();
                              if (areDraftDatesValid) {
                                handleAccept(draftValue);
                                setPickerOpen(false);
                              }
                            }}
                          />
                        </Box>
                      </Tooltip>
                    )}

                    {withIcon && (
                      <IconButton
                        icon="calendar"
                        {...props}
                        size="small"
                        variant="ghost"
                        aria-label="Toggle picker"
                      />
                    )}
                  </Box>
                );
              }
            : () => null,
        calendarHeader: CustomCalendarHeader,
        actionBar: CustomActionBar,
        layout: CustomLayout,
      },
      // Spread otherProps without slotProps so the merged slotProps block below wins
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      ...(({ slotProps: _ignored, ...otherPropsWithoutSlotProps }) => otherPropsWithoutSlotProps)(
        otherProps as any,
      ),
      slotProps: {
        // External slotProps (except textField) spread first so internals win
        ...((otherProps as any).slotProps || {}),
        field: {
          label: labelStart && labelEnd ? `${labelStart} - ${labelEnd}` : '',
        },
        textField: {
          // External textField props (sx, hiddenLabel, inputRef) applied first
          ...((otherProps as any).slotProps?.textField || {}),
          // Internal handlers always win
          error: invalid,
          onChange: handleChange,
          onMouseDown: (e: React.MouseEvent) => {
            // Set the flag before MUI's document-level ClickAwayListener fires so that
            // onClose can ignore the spurious close triggered by clicking the text field.
            // Do NOT set the flag when clicking the toggle icon button — that click should close.
            if (pickerOpen) {
              const target = e.target as HTMLElement;
              if (!target.closest('[aria-label="Toggle picker"]')) {
                ignoreNextCloseRef.current = true;
              }
            }
          },
          onClick: () => {
            if (!disabled && !pickerOpen) {
              setPickerOpen(true);
            }
          },
          onKeyDown: (e: React.KeyboardEvent) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              if (areDraftDatesValid) {
                handleAccept(draftValue);
                setPickerOpen(false);
              }
            }
          },
        },
        popper: {
          ref: dateWrapperRef,
          placement: popperConfig.placement,
          modifiers: [
            {
              name: 'offset',
              options: {
                offset: [0, 16],
              },
            },
            ...(popperConfig.matchWidth
              ? [
                  {
                    name: 'matchWidth',
                    enabled: true,
                    phase: 'beforeWrite' as const,
                    requires: ['computeStyles'],
                    fn: ({ state }: any) => {
                      const width = `${state.rects.reference.width}px`;
                      return {
                        ...state,
                        styles: {
                          ...state.styles,
                          popper: {
                            ...state.styles.popper,
                            width: 'auto',
                            minWidth: width,
                          },
                        },
                      };
                    },
                    effect: ({ state }: any) => {
                      const referenceWidth = (state.elements.reference as HTMLElement).offsetWidth;
                      const popperElement = state.elements.popper as HTMLElement;
                      popperElement.style.minWidth = `${referenceWidth}px`;
                      popperElement.style.width = 'auto';
                    },
                  },
                ]
              : []),
          ],
          sx: {
            borderRadius: '6px',
            boxShadow: theme.shadows[10],
          },
        },
        actionBar: {
          actions: disableReset ? ['cancel', 'accept'] : ['cancel', 'accept', 'clear'],
          withTime,
          mode,
          disableSelection: shouldDenySelectedDate,
          timezone,
          // Pass time update handlers that update draft state
          onStartTimeUpdate: handleStartTimeUpdate,
          onEndTimeUpdate: handleEndTimeUpdate,
          // Pass draft values for TimePicker display
          startValue: startValueForTimePicker,
          endValue: endValueForTimePicker,
          allowApplyWhenUnchanged,
          // Pass validation and change detection state
          areDraftDatesValid,
          hasChanges,
          // Custom handlers for the buttons
          onApplyClick: () => {
            handleAccept(draftValue);
            setPickerOpen(false);
          },
          onCancelClick: () => {
            setDraftValue(dayjsValue);
            setPickerOpen(false);
          },
          onClearClick: () => {
            const clearedValue: [Dayjs | null, Dayjs | null] = [null, null];
            setDraftValue(clearedValue);
            handleAccept(clearedValue);
            setPickerOpen(false);
          },
        },
        layout: {
          withPresets,
          presets,
          timezone,
          onPresetSelect: handlePresetSelect,
          precision: withTime ? ('minute' as PresetPrecision) : ('day' as PresetPrecision),
          selectedPresetId,
          sx: withPresets
            ? {
                borderTopLeftRadius: 0,
                borderBottomLeftRadius: 0,
              }
            : undefined,
        },
        day: (ownerState: any) => ({
          // Format: "Su Nov 01 2020" to match DateInput behavior
          'aria-label': ownerState.day.format('dd MMM DD YYYY'),
        }),
      },
    };

    const pickerComponent = <DateRangePicker {...(commonProps as DateRangePickerProps)} />;

    // If renderInput is provided, wrap the picker with custom input
    if (renderInput) {
      return (
        <Box sx={{ position: 'relative' }}>
          {/* Hide the MUI input visually but keep it functional */}
          <Box
            sx={{
              position: 'absolute',
              opacity: 0,
              pointerEvents: 'none',
              width: '100%',
              zIndex: -1,
            }}
          >
            {pickerComponent}
          </Box>
          {/* Render custom input on top */}
          <Box
            onClick={() => {
              if (!disabled) {
                setPickerOpen(true);
              }
            }}
            sx={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          >
            {renderInput({
              openDatePicker: () => setPickerOpen(true),
              fromFormattedValue,
              toFormattedValue,
              labelStart,
              labelEnd,
              placeholderStart,
              placeholderEnd,
              disabled,
            })}
          </Box>
        </Box>
      );
    }

    return pickerComponent;
  },
);
